export interface CaseState {
    caseId: CaseId
    caseName: string | undefined | null
    isSharedCase: boolean
    caseSegObjectPropertyMap: SegObjectPropertyMap | undefined
    caseSegObjectPropertyMaps: SegObjectPropertyMap[]
    loadedSegObjectPropertyMaps: SegObjectPropertyMap[]
    propertyCatalogByMapId: Record<number, PropertySummary[]>
    /** Property names per map (parallel to propertyCatalogByMapId); reused for single-map shared lists. */
    propertyNamesByMapId: Record<number, string[]>
    markerDimensionsByMapId: Record<number, MarkerDimension[]>
    /** Shared property names across loaded maps (intersection). Summaries stay in propertyCatalogByMapId. */
    sharedPropertyNames: string[]
    caseRcnResults: RecurrentNeighborhoodsResult[]
    caseNeighborhoodEnrichmentResults: NeighborhoodEnrichmentResult[]
    caseCellTypeDetectionResults: CellTypeDetectionResult[]
    caseTsneResults: TsneResult[]
    caseUmapResults: UmapResult[]
    casePlottingResults: any
}

export type CaseId = number | string | null

export interface MarkerDimension {
    segobjectpropertymap: SegObjectPropertyMap
    name: string
    // Bounds for avoiding extra call for data and/or avoiding complex binary data handling
    lower_bound: number
    upper_bound: number
    source_bin: string
    source_json: string
}

export interface PropertySummary {
    name: string
    lower_bound: number
    upper_bound: number
}

export interface SegObjectPropertyMap {
    id: number
    name: string
    source: string
    segSource: string | undefined
}

export interface RecurrentNeighborhoodsResult {
    name: string
    img: string
    download_File: string
    details: {
        keepBackground: boolean
        numberOfRcn: number
        radius: number
        selections: JobResultSelectionRef[]
    }
}

export interface NeighborhoodEnrichmentResult {
    name: string
    img: string
    download_File: string
    details: {
        keepBackground: boolean
        radius: number
        selections: JobResultSelectionRef[]
    }
}

/** Selection refs stored on RCN/NEN job results (legacy string names also appear). */
export type JobResultSelectionRef = string | { id?: string | null; name?: string | null; type?: number | null }

export function formatJobResultSelections(selections: JobResultSelectionRef[] | undefined | null): string {
    if (!selections?.length) return ''
    return selections
        .map(s => (typeof s === 'string' ? s : s.name || s.id || ''))
        .filter(Boolean)
        .join(', ')
}

export interface CellTypeDetectionResult {
    name: string
    image: string
    download: string
    details: {
        name: string
        seg_object_property_map: { name: string; source: string; segSource: string }
        markers: string[]
        morphometrics: string[]
        transcripts: string[]
    }
}

export interface TsneResult {
    name: string
    x: string
    y: string
    normalization: string
    perplexity: number
    number_of_iterations: number
    morphometrics: boolean
    markers: boolean
    transcripts: boolean
    seg_object_property_maps?: IntensityMapRef[]
    /** @deprecated legacy job-result fields; prefer seg_object_property_maps */
    seg_object_property_map_name?: string
    seg_object_property_map_segSource?: string
    seg_object_property_map?: IntensityMapRef
}

export interface UmapResult {
    name: string
    x: string
    y: string
    normalization: string
    markers: boolean
    transcripts: boolean
    morphometrics: boolean
    number_of_neighbors: number
    minimum_distance: number
    spread: number
    seg_object_property_maps?: IntensityMapRef[]
    /** @deprecated legacy job-result fields; prefer seg_object_property_maps */
    seg_object_property_map_name?: string
    seg_object_property_map_segSource?: string
    seg_object_property_map?: IntensityMapRef
}

/** Fields used to identify an intensity map on job results or in loaded state. */
export type IntensityMapRef = {
    id?: number | string
    name?: string | null
    segSource?: string | null
}

function normalizeSegSource(segSource: string): string {
    return segSource.replace(/\/+$/, '')
}

/** All known identities for a map (segSource / id / name) so name-only results still match. */
export function intensityMapKeys(m: IntensityMapRef): string[] {
    const keys: string[] = []
    if (m.segSource) keys.push(`seg:${normalizeSegSource(m.segSource)}`)
    if (m.id != null && m.id !== '') keys.push(`id:${m.id}`)
    if (m.name) keys.push(`name:${m.name}`)
    return keys
}

export function clusteringResultMapRefs(result: {
    seg_object_property_maps?: IntensityMapRef[] | null
    seg_object_property_map?: IntensityMapRef | null
    seg_object_property_map_name?: string
    seg_object_property_map_segSource?: string
}): IntensityMapRef[] {
    if (Array.isArray(result.seg_object_property_maps) && result.seg_object_property_maps.length > 0) {
        return result.seg_object_property_maps
    }
    if (result.seg_object_property_map) {
        return [result.seg_object_property_map]
    }
    if (result.seg_object_property_map_name || result.seg_object_property_map_segSource) {
        return [
            {
                name: result.seg_object_property_map_name,
                segSource: result.seg_object_property_map_segSource,
            },
        ]
    }
    return []
}

/**
 * True when the clustering result's intensity maps are exactly the currently loaded set
 * (same size, each result map aliases a distinct loaded map via segSource / id / name).
 */
export function clusteringResultMatchesLoadedMaps(
    result: Parameters<typeof clusteringResultMapRefs>[0],
    loadedMaps: Iterable<IntensityMapRef>
): boolean {
    const loaded = [...loadedMaps]
    const resultMaps = clusteringResultMapRefs(result)
    if (resultMaps.length === 0 || resultMaps.length !== loaded.length) return false

    const used = new Set<number>()
    for (const resultMap of resultMaps) {
        const resultKeys = new Set(intensityMapKeys(resultMap))
        if (resultKeys.size === 0) return false
        const idx = loaded.findIndex(
            (loadedMap, i) =>
                !used.has(i) && intensityMapKeys(loadedMap).some(key => resultKeys.has(key))
        )
        if (idx < 0) return false
        used.add(idx)
    }
    return used.size === loaded.length
}

/** X/Y axis property names produced by TSNE/UMAP job results. */
export function clusteringAxisPropertyNames(
    results: { x?: string; y?: string }[]
): Set<string> {
    const names = new Set<string>()
    for (const result of results) {
        if (result.x) names.add(result.x)
        if (result.y) names.add(result.y)
    }
    return names
}

/**
 * Keep the first item for each duplicate name (e.g. repeated TSNE_default entries
 * in the experiment dropdown).
 */
export function dedupeClusteringResultsByName<T extends { name: string }>(results: T[]): T[] {
    const seen = new Set<string>()
    return results.filter(result => {
        if (seen.has(result.name)) return false
        seen.add(result.name)
        return true
    })
}

/**
 * Keep non-clustering properties always; keep TSNE/UMAP axis properties only when they
 * belong to a result whose intensity-map set exactly matches the loaded maps.
 */
export function filterCatalogByMatchedClusteringResults<T extends { name: string }>(
    catalog: T[],
    tsne: TsneResult[],
    umap: UmapResult[],
    loadedMaps: Iterable<IntensityMapRef>
): T[] {
    const allClusteringProps = clusteringAxisPropertyNames([...tsne, ...umap])
    if (allClusteringProps.size === 0) return catalog

    const matchedResults = dedupeClusteringResultsByName([
        ...tsne.filter(r => clusteringResultMatchesLoadedMaps(r, loadedMaps)),
        ...umap.filter(r => clusteringResultMatchesLoadedMaps(r, loadedMaps)),
    ])
    const allowedClusteringProps = clusteringAxisPropertyNames(matchedResults)
    return catalog.filter(
        p => !allClusteringProps.has(p.name) || allowedClusteringProps.has(p.name)
    )
}
